/**
 * Token Usage tab — where an org's recorded token spend went, by source project over
 * time, then by user, action and session.
 *
 * Two pieces on purpose: `TokenUsageTabView` is pure and takes every section's state as
 * props (so the whole tab renders under the `node` vitest environment), and
 * `TokenUsageTab` is the container that fetches.
 *
 * Sections load INDEPENDENTLY. Each cut owns its own request, its own try/catch and its
 * own error slot, so a single failing endpoint shows an error card in that section and
 * leaves the rest of the tab rendered. There is deliberately no shared `Promise.all`
 * catch — one previously blanked a whole page here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adminApi,
  type OrganizationUsageSummary,
  type UsageByActionResponse,
  type UsageBySessionResponse,
  type UsageBySourceProjectResponse,
  type UsageByUserResponse,
} from '@/services/adminApi';
import { TokenUsageChart } from './TokenUsageChart';
import { TokenUsageRangeBar } from './TokenUsageRangeBar';
import { TokenUsageSummary } from './TokenUsageSummary';
import {
  ByActionTable,
  ByUserTable,
  SessionPricingPanel,
  TokenUsageSection,
  TokenUsageSectionError,
} from './TokenUsageTables';
import {
  describeDataBegins,
  resolveTokenUsageRange,
  type ResolvedTokenUsageRange,
  type TokenUsageRange,
} from './tokenUsageRange';
import { buildSourceProjectSeries } from './tokenUsageView';

export interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const idleSection = <T,>(): SectionState<T> => ({ data: null, loading: false, error: null });

export interface TokenUsageTabViewProps {
  range: TokenUsageRange;
  onRangeChange: (next: TokenUsageRange) => void;
  /** Client-side range validation message; when set, no request was sent. */
  rangeError: string | null;
  resolvedRange: ResolvedTokenUsageRange | null;
  bySourceProject: SectionState<UsageBySourceProjectResponse>;
  byUser: SectionState<UsageByUserResponse>;
  byAction: SectionState<UsageByActionResponse>;
  bySession: SectionState<UsageBySessionResponse>;
  poolSummary: SectionState<OrganizationUsageSummary>;
}

function SectionBody<T>({
  state,
  errorMessage,
  loadingLabel,
  children,
}: {
  state: SectionState<T>;
  errorMessage: string;
  loadingLabel: string;
  children: (data: T) => React.ReactNode;
}) {
  if (state.error) return <TokenUsageSectionError message={errorMessage} />;
  if (state.data) return <>{children(state.data)}</>;
  if (state.loading) {
    return <p className="text-sm text-muted-foreground py-4">{loadingLabel}</p>;
  }
  return null;
}

export function TokenUsageTabView({
  range,
  onRangeChange,
  rangeError,
  resolvedRange,
  bySourceProject,
  byUser,
  byAction,
  bySession,
  poolSummary,
}: TokenUsageTabViewProps) {
  const spendRange = bySourceProject.data?.range;

  // The boundary note needs a resolved window AND a known oldest record. Any section's
  // range echo would do; the chart's is the one that is always requested first.
  const dataBeginsNote = spendRange
    ? describeDataBegins(spendRange.fromUtc, spendRange.oldestRecordUtc)
    : null;

  const series = useMemo(() => {
    if (!bySourceProject.data) return null;
    const { buckets, range: r } = bySourceProject.data;
    return buildSourceProjectSeries(buckets, r.fromUtc, r.toUtc);
  }, [bySourceProject.data]);

  const orgHasNoRecordsEver =
    !!spendRange && spendRange.oldestRecordUtc === null && bySourceProject.data?.totalTokens === 0;

  return (
    <div className="token-usage-tab">
      <TokenUsageRangeBar value={range} onChange={onRangeChange} error={rangeError} />

      <TokenUsageSummary
        rangedTotalTokens={bySourceProject.data?.totalTokens ?? null}
        poolBalance={poolSummary.data?.usage.tokenBalance ?? null}
        lifetimeConsumed={poolSummary.data?.usage.totalTokensUsed ?? null}
        poolError={poolSummary.error ? 'Failed to load pool counters' : null}
        dataBeginsNote={dataBeginsNote}
      />

      {orgHasNoRecordsEver && (
        <p className="mb-6 text-sm text-muted-foreground">
          This organization has no recorded token usage at all.
        </p>
      )}

      {!resolvedRange && rangeError ? (
        <p className="text-sm text-muted-foreground">
          Adjust the date range to load usage.
        </p>
      ) : (
        <>
          <SectionBody
            state={bySourceProject}
            errorMessage="Failed to load spend by source project"
            loadingLabel="Loading spend by source project…"
          >
            {() => series && <TokenUsageChart series={series} />}
          </SectionBody>

          <TokenUsageSection
            title="By user"
            description="Who the spend was recorded against. Service and pre-migration rows are bucketed separately below the user table."
          >
            <SectionBody
              state={byUser}
              errorMessage="Failed to load spend by user"
              loadingLabel="Loading spend by user…"
            >
              {data => <ByUserTable users={data.users} />}
            </SectionBody>
          </TokenUsageSection>

          <TokenUsageSection
            title="By action"
            description="Rows recorded without an action name are shown, not dropped — hiding them would make these totals disagree with the range total."
          >
            <SectionBody
              state={byAction}
              errorMessage="Failed to load spend by action"
              loadingLabel="Loading spend by action…"
            >
              {data => <ByActionTable actions={data.actions} />}
            </SectionBody>
          </TokenUsageSection>

          <TokenUsageSection
            title="Per-session pricing inputs"
            description="Tokens per session, and tokens per hour over sessions that carry a recorded duration."
          >
            <SectionBody
              state={bySession}
              errorMessage="Failed to load per-session statistics"
              loadingLabel="Loading per-session statistics…"
            >
              {data => (
                <SessionPricingPanel sessions={data.sessions} stats={data.pricingStats} />
              )}
            </SectionBody>
          </TokenUsageSection>
        </>
      )}
    </div>
  );
}

export interface TokenUsageTabProps {
  orgId: string;
}

export function TokenUsageTab({ orgId }: TokenUsageTabProps) {
  const [range, setRange] = useState<TokenUsageRange>({ preset: '30d' });
  const [bySourceProject, setBySourceProject] = useState(
    idleSection<UsageBySourceProjectResponse>()
  );
  const [byUser, setByUser] = useState(idleSection<UsageByUserResponse>());
  const [byAction, setByAction] = useState(idleSection<UsageByActionResponse>());
  const [bySession, setBySession] = useState(idleSection<UsageBySessionResponse>());
  const [poolSummary, setPoolSummary] = useState(idleSection<OrganizationUsageSummary>());

  // One monotonic counter across every range-scoped fetch, so a slow response from a
  // superseded range can never overwrite the current one.
  const seqRef = useRef(0);

  const { resolved, error: rangeError } = useMemo(
    () => resolveTokenUsageRange(range, new Date()),
    [range]
  );
  const fromDate = resolved?.fromDate;
  const toDate = resolved?.toDate;

  const loadSection = useCallback(
    async <T,>(
      seq: number,
      fetcher: () => Promise<T>,
      setState: (next: SectionState<T>) => void,
      label: string
    ) => {
      setState({ data: null, loading: true, error: null });
      try {
        const data = await fetcher();
        if (seq !== seqRef.current) return;
        setState({ data, loading: false, error: null });
      } catch (err) {
        if (seq !== seqRef.current) return;
        console.error(`Failed to load token usage: ${label}`, err);
        setState({ data: null, loading: false, error: label });
      }
    },
    []
  );

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const seq = ++seqRef.current;
    const params = { fromDate, toDate };

    // Fired concurrently, each with its own error slot — no shared catch.
    void loadSection(
      seq,
      () => adminApi.getOrgUsageBySourceProject(orgId, params),
      setBySourceProject,
      'by-source-project'
    );
    void loadSection(
      seq,
      () => adminApi.getOrgUsageByUser(orgId, params),
      setByUser,
      'by-user'
    );
    void loadSection(
      seq,
      () => adminApi.getOrgUsageByAction(orgId, params),
      setByAction,
      'by-action'
    );
    void loadSection(
      seq,
      () => adminApi.getOrgUsageBySession(orgId, params),
      setBySession,
      'by-session'
    );
  }, [orgId, fromDate, toDate, loadSection]);

  // Pool counters are lifetime, so they are fetched once per org rather than per range.
  useEffect(() => {
    let cancelled = false;
    setPoolSummary({ data: null, loading: true, error: null });
    adminApi
      .getOrganizationUsageSummary(orgId)
      .then(data => {
        if (!cancelled) setPoolSummary({ data, loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load token usage: pool counters', err);
        setPoolSummary({ data: null, loading: false, error: 'usage-summary' });
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return (
    <TokenUsageTabView
      range={range}
      onRangeChange={setRange}
      rangeError={rangeError}
      resolvedRange={resolved}
      bySourceProject={bySourceProject}
      byUser={byUser}
      byAction={byAction}
      bySession={bySession}
      poolSummary={poolSummary}
    />
  );
}

export default TokenUsageTab;
